import { getBFFApiClient } from '@acp/feature/auth';
import {
    IntegratedProperty, AgencyPortfolioProperty, APIResponse, IntegratedPropertyManager,
    ServiceOption,
    EditComplianceFormProperty,
} from '@acp/shared';
import HttpStatusCodes from 'http-status-codes';

async function markNewManagementAsUnderEnrolment(agencyPortfolioPropertyId: number) {
    const response = await getBFFApiClient().post<APIResponse<null>>(
        `/v1/portfolio/new-management/${agencyPortfolioPropertyId}/under-enrolment`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to mark new management as under enrolment');
    }

    return response.data.data;
}

async function dismissNewManagement(agencyPortfolioPropertyId: number) {
    const response = await getBFFApiClient().post<APIResponse<null>>(
        `/v1/portfolio/new-management/${agencyPortfolioPropertyId}/dismiss`
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to dismiss new management');
    }

    return response.data.data;
}

async function getIntegratedProperties({
    propertyManager,
    serviceProvider,
    services,
    facets,
    pageNumber,
    pageSize,
    query,
}: {
    facets: string[];
    services: string[];
    propertyManager: string | null;
    serviceProvider: string | null;
    pageNumber: number;
    pageSize: number;
    query: string | null;
}) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryParams: any = {
        'property-manager': propertyManager,
        'provided-by': serviceProvider,
        services,
        facet: facets,
        page: pageNumber,
        query: query ?? '',
        'page-size': pageSize,
    };

    const response = await getBFFApiClient().get<APIResponse<{
        total: number;
        items: IntegratedProperty[];
        unfilteredTotal: number;
    }>>('/v1/portfolio/integrated', {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        params: queryParams,
    });

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get integrated properties: ${response.data.error}`);
    }

    return response.data.data;
}

async function exportIntegratedProperties({
    fileName,
    propertyManager,
    serviceProvider,
    services,
    facets,
    query,
}: {
    fileName: string;
    facets: string[];
    services: string[];
    propertyManager: string | null;
    serviceProvider: string | null;
    query: string | null;
}) {
    const queryParams = {
        fileName,
        'property-manager': propertyManager,
        'provided-by': serviceProvider,
        services,
        facet: facets,
        query: query ?? '',
    };

    const response = await getBFFApiClient().get<BlobPart>('/v1/portfolio/integrated/export', {
        params: queryParams,
        responseType: 'arraybuffer',
    });

    if (response.status !== HttpStatusCodes.OK) {
        const errorMessage = typeof response.headers.get === 'function'
            && response.headers.get('x-error-message');

        throw new Error(errorMessage?.toString() ?? 'Failed to export integrated properties');
    }

    return new Blob([response.data], { type: 'application/vnd.ms-excel' });
}

async function getIntegratedPropertyManagers() {
    const response = await getBFFApiClient().get<APIResponse<IntegratedPropertyManager[]>>(
        '/v1/portfolio/integrated/property-managers'
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get integrated property managers');
    }

    return response.data.data;
}

async function getIntegratedServiceProviders() {
    const response = await getBFFApiClient().get<APIResponse<string[]>>(
        '/v1/portfolio/integrated/service-providers'
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get integrated service providers');
    }

    return response.data.data;
}

async function getComplianceAgencyServiceProviders() {
    const response = await getBFFApiClient().get<APIResponse<ServiceOption[]>>(
        '/v1/portfolio/integrated/list-service-providers'
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error(`Failed to get compliance agency service providers: status=${response.status} success=${response.data.success}`);
    }

    return response.data.data;
}

async function addComplianceAgencyServiceProvider(
    supplierName: string
) {
    const response = await getBFFApiClient().post<APIResponse<ServiceOption>>(
        '/v1/portfolio/integrated/service-providers',
        {
            supplierName,
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to add compliance agency service provider');
    }

    return response.data.data;
}

async function getNewManagements({
    page,
}: {
    page: number;
}) {
    const response = await getBFFApiClient().get<APIResponse<{
        total: number;
        items: AgencyPortfolioProperty[];
    }>>(
        '/v1/portfolio/analytics/new-managements',
        {
            params: {
                page,
            },
        }
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to get new managements');
    }

    return response.data.data;
}

async function submitCompliances(properties: EditComplianceFormProperty[]) {
    const response = await getBFFApiClient().patch<APIResponse<null>>(
        '/v1/portfolio/integrated',
        properties
    );

    if (response.status !== HttpStatusCodes.OK || !response.data.success) {
        throw new Error('Failed to submit compliances');
    }

    return response.data.data;
}

export {
    dismissNewManagement,
    markNewManagementAsUnderEnrolment,
    getIntegratedPropertyManagers,
    getIntegratedProperties,
    getNewManagements,
    getIntegratedServiceProviders,
    getComplianceAgencyServiceProviders,
    exportIntegratedProperties,
    submitCompliances,
    addComplianceAgencyServiceProvider,
};
